/* eslint-disable react-hooks/exhaustive-deps */
import { LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { useAuthState, useReviewRequestState, useReviewState } from '@core/configureStore';
import { RequestServiceStage, ReviewRequest, StarRating } from '@core/models';
import { Button, Card, Col, Divider, List, Progress, Row, Space } from 'antd';
import LineChart from '@components/Chart/LineChart';
import { formatUtcDate } from '@utils/date.util';
import React, { useMemo } from 'react';
import moment from 'moment';
import ServiceStatusTag from '@components/ServiceStatusTag';
import ReviewTable from '@components/ReviewTable';
import AnimatedComponent from '@components/AnimatedComponent';
import { useAppNavigation } from '@core/hooks';
import LoadableContainer from '@components/LoadableContainer';
import NoBusinessFound from '@components/NoBusinessFound';
import NumberOfReviewsWidget from '@components/NumberOfReviewsWidget';
import AverageRatingWidget from '@components/AverageRatingWidget';

type Props = {};

const DashboardScreen = (props: Props) => {
  const { navigate } = useAppNavigation();
  const { user } = useAuthState();
  const { reviewResponse, isLoading: isLoadingReview } = useReviewState();
  const { requests, isLoading: isLoadingRequest } = useReviewRequestState();

  const reviewStarRatings: number[] = useMemo(
    () =>
      (reviewResponse.reviews || []).map(
        review => StarRating[review?.starRating.valueOf()] as any
      ) || [],
    [reviewResponse]
  );

  const reviewsByStars = useMemo(
    () =>
      new Array(5)
        .fill(0)
        .map((_, i) => reviewStarRatings.filter(starRating => starRating === i + 1).length),
    [reviewStarRatings]
  );

  const moreThan4StarReviews: number[] = useMemo(
    () => reviewStarRatings.filter(starRating => starRating >= 4),
    [reviewStarRatings]
  );

  const sentRequests = useMemo(
    () => requests.filter((req: ReviewRequest) => req.email_status !== RequestServiceStage.Pending),
    [requests]
  );

  const repliedRequests = useMemo(
    () =>
      requests.filter((req: ReviewRequest) => req.email_status === RequestServiceStage.Reviewed),
    [requests]
  );

  const requestRatings = useMemo(() => repliedRequests.map(req => req.rating), [repliedRequests]);
  const averageRequestRating = useMemo(() => {
    return requestRatings?.length > 0
      ? requestRatings.reduce((rA: any, rB: any) => rA + rB) / (requestRatings?.length || 1)
      : 0;
  }, []);
  const totalSentRequest = useMemo(() => sentRequests?.length, [sentRequests]);
  const totalRepliedRequest = useMemo(() => repliedRequests?.length, [repliedRequests]);

  const emailSMSActivity = useMemo(() => {
    const activityFeed: {
      status: RequestServiceStage;
      name: string;
      date: string;
      contact: string;
    }[] = [];
    if (requests) {
      for (let request of requests) {
        activityFeed.push({
          status: request.email_status,
          name: request.contact,
          date: request.last_modified_date,
          contact: request.contact,
        });
      }
    }
    return activityFeed.sort(service => moment(service.date).unix());
  }, [requests]);

  const handleViewMoreReviews = () => {
    navigate('reviews');
  };

  return (
    <div style={{ padding: '10px 20px 100px 20px' }}>
      <LoadableContainer isLoading={!user?.selected_location} loadComponent={<NoBusinessFound />}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Row gutter={10}>
            <Col span={6}>
              <AnimatedComponent.OpacityFadeInDiv delay={200}>
                <NumberOfReviewsWidget />
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
            <Col span={6}>
              <AnimatedComponent.OpacityFadeInDiv delay={200}>
                <AverageRatingWidget />
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
            <Col span={12}>
              <AnimatedComponent.OpacityFadeInDiv delay={200}>
                <Card>
                  <Row gutter={10}>
                    <Col span={6}>
                      <h1>Request sent</h1>
                      <div style={{ marginTop: 10 }}>
                        {isLoadingRequest ? <LoadingOutlined /> : <h1>{totalSentRequest}</h1>}
                      </div>
                    </Col>
                    <Col span={6}>
                      <h1>Reached</h1>
                      <div style={{ marginTop: 10 }}>
                        {isLoadingRequest ? <LoadingOutlined /> : <h1>{totalRepliedRequest}</h1>}
                      </div>
                    </Col>
                    <Col span={6}>
                      <h1>Avg. Rating</h1>
                      <div style={{ marginTop: 10 }}>
                        {isLoadingRequest ? <LoadingOutlined /> : <h1>{averageRequestRating}</h1>}
                      </div>
                    </Col>
                    <Col span={6}>
                      <h1>Haven't replied</h1>
                      <div style={{ marginTop: 10 }}>
                        {isLoadingRequest ? (
                          <LoadingOutlined />
                        ) : (
                          <h1
                            style={{
                              color: totalSentRequest - totalRepliedRequest > 0 ? 'red' : 'black',
                            }}>
                            {totalSentRequest - totalRepliedRequest}
                          </h1>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={16}>
              <AnimatedComponent.OpacityFadeInDiv delay={250}>
                <Card>
                  <LineChart />
                </Card>
              </AnimatedComponent.OpacityFadeInDiv>
              <AnimatedComponent.OpacityFadeInDiv delay={300}>
                <Card style={{ marginTop: 10 }}>
                  <ReviewTable
                    reviews={reviewResponse?.reviews || []}
                    size="small"
                    partition={{ start: 0, end: 5 }}
                  />
                  {reviewResponse.reviews?.length > 0 && !isLoadingReview && (
                    <Button
                      style={{ marginTop: 10, width: '100%' }}
                      onClick={handleViewMoreReviews}>
                      View more reviews
                    </Button>
                  )}
                </Card>
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
            <Col span={8}>
              <AnimatedComponent.OpacityFadeInDiv delay={250}>
                <Card>
                  <h1>Satisfaction Rate</h1>
                  <p>Number of review which has more than 3 stars</p>
                  <Row style={{ marginTop: 30 }}>
                    <Col span={12}>
                      {isLoadingRequest ? (
                        <LoadingOutlined />
                      ) : (
                        <Progress
                          style={{ marginTop: 10, width: '100%' }}
                          type="dashboard"
                          percent={parseFloat(
                            (
                              (moreThan4StarReviews.length / reviewStarRatings.length) *
                              100
                            ).toFixed(1)
                          )}
                        />
                      )}
                    </Col>
                    <Col span={12}>
                      <div>
                        {new Array(5).fill(0).map((_, star) => (
                          <Space>
                            <p>{star + 1}</p>
                            <Progress
                              showInfo={false}
                              strokeColor="#fadb14"
                              style={{ width: '100px' }}
                              percent={
                                (reviewsByStars[star] / reviewResponse.totalReviewCount) * 100
                              }
                            />
                            <p>{reviewsByStars[star]}</p>
                          </Space>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </AnimatedComponent.OpacityFadeInDiv>
              <AnimatedComponent.OpacityFadeInDiv delay={300}>
                <Card style={{ marginTop: 10 }}>
                  <h1>Email & SMS Activity</h1>
                  <p>Tracking progress of request email and SMS</p>
                  <Divider />
                  <div style={{ marginTop: 20 }}>
                    {isLoadingRequest ? (
                      <LoadingOutlined />
                    ) : (
                      <List
                        itemLayout="horizontal"
                        dataSource={emailSMSActivity.slice(0, 5)}
                        renderItem={(activity, index) => (
                          <List.Item key={index}>
                            <List.Item.Meta
                              avatar={<MailOutlined />}
                              title={activity.name}
                              description={
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <div>{formatUtcDate(activity.date)}</div>
                                  <ServiceStatusTag status={activity.status} />
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )}
                  </div>
                </Card>
              </AnimatedComponent.OpacityFadeInDiv>
            </Col>
          </Row>
        </Space>
      </LoadableContainer>
    </div>
  );
};

export default DashboardScreen;
